import React, { useState, useEffect } from 'react';
import Sidebar from '../ui/Sidebar';
import UserProfileDropdown from '../ui/UserProfileDropdown';
import ProjectContextBar from '../ui/ProjectContextBar';
import QuickActionButton from '../ui/QuickActionButton';
import NotificationBell from '../ui/NotificationBell';
import LoginNotificationPopup from '../ui/LoginNotificationPopup';
import RealtimeNotificationPopup from '../ui/RealtimeNotificationPopup';
import MotivationalPopup, { MotivationalBanner } from '../ui/MotivationalPopup';
import { useAuth } from '../../contexts/AuthContext';
import useLoginNotifications from '../../hooks/useLoginNotifications';
import useMotivationalMessage from '../../hooks/useMotivationalMessage';
import useRealtimeNotifications from '../../hooks/useRealtimeNotifications';

const MainLayout = ({ children, showProjectContext = false, currentProject = null }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user, userProfile } = useAuth();

  const {
    showPopup,
    pendingNotifications,
    hasPersistentBadge: loginBadge,
    handleAction,
    handleDismiss,
  } = useLoginNotifications(user?.id);

  const {
    currentPopup: realtimePopup,
    hasPersistentBadge: realtimeBadge,
    handleAction: handleRealtimeAction,
    handleDismiss: handleRealtimeDismiss,
  } = useRealtimeNotifications(user?.id);

  const {
    showPopup: showMotivationalPopup,
    showBanner: showMotivationalBanner,
    message: motivationalMessage,
    handleDismiss: handleMotivationalDismiss,
    handleCloseBanner: handleMotivationalCloseBanner,
  } = useMotivationalMessage(user?.id);

  // Combined persistent badge: login badge OR realtime badge
  const hasPersistentBadge = loginBadge || realtimeBadge;

  // Determine which popup is active — priority: login > realtime > motivational
  const loginPopupActive = showPopup;
  const realtimePopupActive = !!realtimePopup && !loginPopupActive;
  const motivationalPopupActive = showMotivationalPopup && !loginPopupActive && !realtimePopupActive;

  const currentUserData = {
    name: userProfile?.full_name || userProfile?.name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'User',
    role: userProfile?.role ? (userProfile?.role?.charAt(0)?.toUpperCase() + userProfile?.role?.slice(1)) : 'Member',
    email: user?.email || ''
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setIsMobileSidebarOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleToggleSidebar = () => {
    if (isMobile) {
      setIsMobileSidebarOpen(prev => !prev);
    } else {
      setIsSidebarCollapsed(prev => !prev);
    }
  };

  const handleCloseMobileSidebar = () => {
    if (isMobile) setIsMobileSidebarOpen(false);
  };

  const sidebarCollapsed = isMobile ? true : isSidebarCollapsed;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      {isMobile && isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={handleCloseMobileSidebar}
        />
      )}

      <Sidebar
        isCollapsed={sidebarCollapsed}
        isMobileOpen={isMobileSidebarOpen}
        onToggleCollapse={handleToggleSidebar}
      />

      <div
        className={`transition-all duration-smooth ${
          !isMobile && !isSidebarCollapsed ? 'lg:ml-60' : !isMobile ? 'lg:ml-20' : 'ml-0'
        }`}
      >
        <header className="sticky top-0 z-30 w-full bg-card/80 backdrop-blur-xl border-b border-border shadow-sm">
          <div className="flex items-center justify-between h-16 px-6">
            <button
              onClick={handleToggleSidebar}
              className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
              aria-label="Toggle menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <div className="flex-1 lg:flex-none" />
            <div className="flex items-center gap-2">
              <NotificationBell persistentBadge={hasPersistentBadge} />
              <UserProfileDropdown currentUser={currentUserData} />
            </div>
          </div>
        </header>

        {showProjectContext && currentProject && (
          <ProjectContextBar project={currentProject} />
        )}

        <main className="p-6 lg:p-8 animate-fade-in">
          {showMotivationalBanner && motivationalMessage && (
            <div className="mb-6">
              <MotivationalBanner
                message={motivationalMessage}
                userName={currentUserData?.name}
                onClose={handleMotivationalCloseBanner}
              />
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Login-time pending tasks popup */}
      {loginPopupActive && (
        <LoginNotificationPopup
          notifications={pendingNotifications}
          onAction={handleAction}
          onDismiss={handleDismiss}
        />
      )}

      {/* Real-time workflow notification popup — shown immediately when a new task is assigned */}
      {realtimePopupActive && (
        <RealtimeNotificationPopup
          notification={realtimePopup}
          onAction={handleRealtimeAction}
          onDismiss={handleRealtimeDismiss}
        />
      )}

      {/* Motivational popup — shown after workflow popups clear */}
      {motivationalPopupActive && (
        <MotivationalPopup
          message={motivationalMessage}
          userName={currentUserData?.name}
          onDismiss={handleMotivationalDismiss}
        />
      )}

      <QuickActionButton />
    </div>
  );
};

export default MainLayout;